import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import { FieldSearchIcon } from "../labInvestigations/labIcons";
import AllergenCatalogService from "./AllergenCatalogService";

/**
 * Debounced federated fuzzy-search dropdown for the allergen catalog. Same
 * shape as physicalActivity/ActivityCatalogAutocomplete but hits the allergen
 * endpoint which fans out to api-medicine-service /medicine/salts (distinct
 * salt names) + own catalog (foods + other).
 *
 * On pick, hands the parent a snapshot: `{name, category, catalogId?, mCode?,
 * source}`. `source: "salt"` rows land with category=Salt and no mCode
 * (salts aren't catalog-code-addressable). Typing without picking is
 * allowed (so the parent can render the query as the current `name`), but
 * the catalog identifiers are cleared — the parent's Save gate insists on
 * a real dropdown pick, so free-text entries can never reach the wire.
 */
const DEBOUNCE_MS = 250;

const dedupeByKey = (rows) => {
  const seen = new Set();
  const out = [];
  for (const r of rows || []) {
    const key = ((r && r.name) || "").toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
};

const AllergenCatalogAutocomplete = ({
  value,
  catalogId,
  mCode,
  onChange,
  disabled,
  isAlreadyActive,
}) => {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);
  const serviceRef = useRef(null);

  if (!serviceRef.current) serviceRef.current = new AllergenCatalogService();

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  // Close on outside mousedown OR Escape. Listeners run only while the
  // dropdown is open so the picker doesn't hold document listeners the
  // rest of its life — same pattern the ICD picker and reactions
  // picker use.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const openDropdown = () => {
    setOpen(true);
    if (results.length === 0 && !loading) fetchResults("");
  };

  const fetchResults = (q, { openWhenDone = true } = {}) => {
    setLoading(true);
    serviceRef.current.search(
      q,
      (data) => {
        setResults(dedupeByKey(Array.isArray(data) ? data : []));
        setLoading(false);
        if (openWhenDone) setOpen(true);
      },
      () => {
        setResults([]);
        setLoading(false);
      }
    );
  };

  // Mount-time prefetch: warm results for a seeded `value` (typically
  // AI-populated in the transcript flow) so the FIRST focus opens the
  // dropdown with hits already loaded. Auto-picks when the AI's string
  // is an exact case-insensitive match to one catalog row; skips when
  // the parent already carries a catalogId/mCode (click-to-edit path)
  // or when the exact match is already-active on this patient.
  const didInitialSearch = useRef(false);
  useEffect(() => {
    if (didInitialSearch.current) return;
    const seed = (value || "").trim();
    if (!seed) return;
    if (catalogId || mCode) return;
    didInitialSearch.current = true;
    setLoading(true);
    serviceRef.current.search(
      seed,
      (data) => {
        const rows = dedupeByKey(Array.isArray(data) ? data : []);
        setResults(rows);
        setLoading(false);
        const exact = rows.find(
          (r) => (r.name || "").trim().toLowerCase() === seed.toLowerCase()
        );
        if (!exact) return;
        const alreadyActive = isAlreadyActive ? !!isAlreadyActive(exact) : false;
        if (alreadyActive) return;
        onChange({
          name: exact.name || "",
          category: exact.category || "Other",
          catalogId: exact.source === "catalog" ? exact.id : "",
          mCode: exact.source === "salt" ? exact.mCode : "",
          source: exact.source || "free-text",
        });
      },
      () => {
        setLoading(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleType = (e) => {
    const q = e.target.value;
    setQuery(q);
    // Free-text edit drops the catalog link — parent's catalogId/mCode/source
    // reset so the row saves as free-text.
    onChange({ name: q, category: "Other", catalogId: "", mCode: "", source: "free-text" });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(q), DEBOUNCE_MS);
  };

  const pick = (item) => {
    setQuery(item.name || "");
    setOpen(false);
    onChange({
      name: item.name || "",
      category: item.category || "Other",
      catalogId: item.source === "catalog" ? item.id : "",
      mCode: item.source === "salt" ? item.mCode : "",
      source: item.source || "free-text",
    });
  };

  const currentKey = catalogId || mCode;

  return (
    <div className="al3-ac" ref={boxRef}>
      {/* Board 3071:3247: magnifier sits on the RIGHT of the field. */}
      <div className="al3-searchbox">
        <input
          type="text"
          value={query}
          onChange={handleType}
          onFocus={openDropdown}
          placeholder="Search allergens…"
          disabled={disabled}
        />
        <span className="al3-search-icon" aria-hidden="true"><FieldSearchIcon /></span>
      </div>
      {open && (
        <ul className="li3-dd" role="listbox">
          {loading && <li className="li3-dd-empty">Searching…</li>}
          {!loading && results.length === 0 && (
            <li className="li3-dd-empty">
              No matches — refine the search to pick from the list.
            </li>
          )}
          {!loading &&
            results.map((r) => {
              const key = r.source === "salt" ? r.mCode : r.id;
              const alreadyActive = isAlreadyActive ? !!isAlreadyActive(r) : false;
              return (
                <li
                  key={key || r.name}
                  role="option"
                  aria-selected={currentKey && key === currentKey}
                  aria-disabled={alreadyActive}
                  className={`li3-dd-opt al3-dd-opt${alreadyActive ? " is-disabled" : ""}`}
                  onClick={() => {
                    // Silent no-op — the "already added" pill + greyed
                    // row already tell the reviewer why. Update the row
                    // from the section table instead.
                    if (alreadyActive) return;
                    pick(r);
                  }}
                  title={
                    alreadyActive
                      ? "Already in the allergies list — use its row menu to update"
                      : undefined
                  }
                >
                  <span className="al3-dd-name">{r.name}</span>
                  {/* Right-side stack: category chip on top, state pill
                      (already added) directly beneath. Category uses the
                      same purple `alg-locked-tag` styling the picked
                      allergen chip carries, so the dropdown option and
                      the final picked chip read as one visual — matches
                      the Physical activity picker's pattern. */}
                  <span className="cc-icd-side">
                    {r.category && (
                      <span className="alg-locked-tag">{r.category}</span>
                    )}
                    {alreadyActive && (
                      <span className="cc-icd-active-tag" aria-hidden="true">
                        already added
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
};

AllergenCatalogAutocomplete.propTypes = {
  value: PropTypes.string,
  catalogId: PropTypes.string,
  mCode: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  /** Predicate (row → bool). When true the option renders greyed with an
      "already added" pill and clicks silently no-op. */
  isAlreadyActive: PropTypes.func,
};

export default AllergenCatalogAutocomplete;
